import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface BlockProps {
	heading: string;
	intro: string;
	points: string[];
	current?: boolean;
}

/** One of the two explanations, tinted when it describes the section you are on. */
function InfoBlock({ heading, intro, points, current }: BlockProps) {
	return (
		<Box
			sx={{
				padding: '14px 16px',
				borderRadius: '12px',
				backgroundColor: current ? 'background.default' : 'transparent',
			}}
		>
			<Typography
				variant='body1'
				sx={{ fontWeight: 'bold', color: 'common.neutral20' }}
			>
				{heading}
			</Typography>
			<Typography
				variant='body2'
				sx={{ color: 'common.neutral50', marginTop: '2px' }}
			>
				{intro}
			</Typography>
			<Box
				component='ul'
				sx={{
					margin: '8px 0 0',
					paddingLeft: '20px',
					color: 'common.neutral30',
					'& li': { marginTop: '6px' },
					'& li::marker': { color: 'common.neutral70' },
				}}
			>
				{points.map((point) => (
					<Typography
						key={point}
						component='li'
						variant='body2'
						sx={{ lineHeight: 1.55 }}
					>
						{point}
					</Typography>
				))}
			</Box>
		</Box>
	);
}

/**
 * The "i" next to the Family / carers information heading. Explains how this
 * section differs from the Manage family member tab, which staff otherwise
 * confuse because both list relatives.
 *
 * Shown on hover: the icon sits inside an AccordionSummary, whose own click
 * target swallows or double-handles clicks meant for a popover.
 */
export default function FamilyCarersInfoHint() {
	const { t } = useTranslation();

	const content = (
		<Box sx={{ padding: '18px 8px 14px' }}>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 'bold',
					color: 'common.neutral20',
					padding: '0 16px 14px',
					borderBottom: '1px solid',
					borderColor: 'common.neutral90',
				}}
			>
				{t('preAdmission.familyCarersInfoTitle')}
			</Typography>

			<Box sx={{ marginTop: '8px' }}>
				<InfoBlock
					current
					heading={t('preAdmission.familyCarersInfoSectionHeading')}
					intro={t('preAdmission.familyCarersInfoSectionIntro')}
					points={[
						t('preAdmission.familyCarersInfoSectionPoint1'),
						t('preAdmission.familyCarersInfoSectionPoint2'),
						t('preAdmission.familyCarersInfoSectionPoint3'),
						t('preAdmission.familyCarersInfoSectionPoint4'),
					]}
				/>

				<InfoBlock
					heading={t('preAdmission.familyCarersInfoManageHeading')}
					intro={t('preAdmission.familyCarersInfoManageIntro')}
					points={[
						t('preAdmission.familyCarersInfoManagePoint1'),
						t('preAdmission.familyCarersInfoManagePoint2'),
					]}
				/>
			</Box>
		</Box>
	);

	return (
		<Tooltip
			title={content}
			arrow
			placement='bottom-start'
			enterDelay={200}
			leaveDelay={100}
			enterTouchDelay={0}
			leaveTouchDelay={15000}
			slotProps={{
				// Repaint MUI's dark hint bubble as a card in the portal's own
				// language: white surface, 16px radius, hairline border, soft shadow.
				tooltip: {
					sx: {
						width: '480px',
						maxWidth: 'calc(100vw - 48px)',
						padding: 0,
						backgroundColor: 'common.white',
						color: 'common.neutral30',
						borderRadius: '16px',
						border: '1px solid',
						borderColor: 'common.neutral90',
						boxShadow: '0px 8px 24px rgba(33, 37, 41, 0.12)',
					},
				},
				arrow: {
					sx: {
						color: 'common.white',
						'&::before': {
							border: '1px solid',
							borderColor: 'common.neutral90',
						},
					},
				},
			}}
		>
			<IconButton
				size='small'
				disableRipple
				aria-label={t('preAdmission.familyCarersInfoTitle')}
				// The heading is an AccordionSummary, so a click on the icon must not
				// bubble up and collapse the section.
				onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}
				sx={{
					marginLeft: '6px',
					color: 'common.neutral50',
					'&:hover': {
						color: 'common.neutral20',
						backgroundColor: 'transparent',
					},
				}}
			>
				<InfoOutlinedIcon fontSize='small' />
			</IconButton>
		</Tooltip>
	);
}
